// Слайдшоу.

import Slideshow, {SlideshowChangeEvent} from '~/app/elements/Slideshow';
import addSwipeEventListeners from '~/app/utils/addSwipeEventListeners';
import {getCurrentScreenMode, ScreenMode} from '~/app/utils/Screen';

/**
 * Класс обратного направления перелистывания.
 */
const CLASS_BACKWARDS = 'backwards';
/**
 * Скрипты слайдшоу завершили инициализацию.
 */
const CLASS_READY = 'ready';
/**
 * Класс текущей секции.
 */
const CLASS_SECTION_CURRENT = 'current';

/**
 * Длительность показа слайда при автоматическом перелистывании.
 */
const SLIDESHOW_DURATION = 15000;

/**
 * Слайдшоу.
 */
function main(): void
{
	const slideshowElements = document.querySelectorAll(
		'main>section.environment>div.slideshow,'
		+ 'main>section.component>div.slideshow',
	) as NodeListOf<HTMLElement>;
	
	for ( const container of Array.from( slideshowElements ) )
	{
		if ( container )
		{
			init( container );
		}
	}
}

/**
 * Инициализация элемента слайдшоу.
 * 
 * @param container Контейнер слайдшоу.
 */
function init( container: HTMLElement ): void
{
	const slidesList = container.querySelector( 'ul.slides' ) as HTMLElement;
	const controls = container.querySelector( 'div.controls' ) as HTMLElement;
	const btnPrev = controls.querySelector( 'button.prev' ) as HTMLElement;
	const btnNext = controls.querySelector( 'button.next' ) as HTMLElement;
	const currentSlide = controls.querySelector( 'span.counter>span.current' ) as HTMLElement;
	const slidesCount = controls.querySelector( 'span.counter>span.total' ) as HTMLElement;
	const sections = container.querySelectorAll( 'div.sections>section' ) as NodeListOf<HTMLElement>;
	
	const slideshow = new Slideshow( slidesList );

	let slideshowPauseCount: number = 0;
	let slideshowHovered: boolean = false;
	
	const playSlideshow = ( withHover: boolean = false ) =>
	{
		if ( withHover )
		{
			slideshowHovered = false;
		}
		else
		{
			slideshowPauseCount--;
		}
		
		if (
			!slideshowHovered
			&& ( slideshowPauseCount <= 0 )
		)
		{
			slideshowPauseCount = 0;
			slideshow.autoSlideshow( SLIDESHOW_DURATION );
		}
	};

	const stopSlideshow = ( withHover: boolean = false ) =>
	{
		if ( withHover )
		{
			slideshowHovered = true;
		}
		else
		{
			slideshowPauseCount++;
		}
		
		slideshow.autoSlideshow( 0 );
	};
	
	let changedByButton: boolean = false;
	
	slidesList.addEventListener(
		Slideshow.EVENT_CHANGE,
		( event: SlideshowChangeEvent ) =>
		{
			// При автоматическом листании направление всегда «вперёд».
			if ( !changedByButton )
			{
				slidesList.classList.remove( CLASS_BACKWARDS );
			}
			
			changedByButton = false;
			
			currentSlide.textContent = String( event.detail.currentSlide + 1 );
			slidesCount.textContent = String( event.detail.slidesCount );
			changeSection(
				sections,
				event.detail.currentSlide,
				event.detail.prevSlide,
			);
		},
	);
	
	const toNext = (): void =>
	{
		changedByButton = true;
		slidesList.classList.remove( CLASS_BACKWARDS );
		slideshow.next();
	};
	
	const toPrev = (): void =>
	{
		changedByButton = true;
		slidesList.classList.add( CLASS_BACKWARDS );
		slideshow.prev();
	};
	
	btnPrev.addEventListener( 'click', toPrev );
	btnNext.addEventListener( 'click', toNext );
	
	slideshow.toSlide( 0 );
	playSlideshow();

	slidesList.addEventListener( 'mouseenter', () => stopSlideshow( true ) );
	slidesList.addEventListener( 'mouseleave', () => playSlideshow( true ) );
	
	addSwipeEventListeners(
		slidesList,
		{
			right: toPrev,
			left: toNext,
		},
	);
	
	container.classList.add( CLASS_READY );
	
	initSectionsResize( sections );
}

/**
 * Смена секций синхронно слайдам.
 * 
 * @param sections Перечень секций.
 * @param currentSlideIndex Индекс текущего слайда.
 * @param prevSlideIndex Индекс предыдущего слайда.
 */
function changeSection(
	sections: NodeListOf<HTMLElement>,
	currentSlideIndex: number,
	prevSlideIndex: number,
): void
{
	const prevSection = sections[prevSlideIndex];
	const currentSection = sections[currentSlideIndex];
	
	if ( prevSection )
	{
		prevSection.classList.remove( CLASS_SECTION_CURRENT );
	}
	
	if ( currentSection )
	{
		currentSection.classList.add( CLASS_SECTION_CURRENT );
	}
}

/**
 * Пересчёт размера блока секций при изменении размера экрана.
 * 
 * @param sections Перечень секций.
 */
function initSectionsResize( sections: NodeListOf<HTMLElement> ): void
{
	// tslint:disable-next-line:no-non-null-assertion
	if ( sections[0] )
	{
		const parent = sections[0].parentElement!;
	
		const onFrame = (): void =>
		{
			let highestHeight = 0;
			
			for ( let i = 0, n = sections.length; i < n; i++ )
			{
				const height = sections[i].offsetHeight;
				
				if ( height > highestHeight )
				{
					highestHeight = height;
				}
			}
			
			parent.style.height = highestHeight + 'px';
		};
		
		let screenMode: ScreenMode = getCurrentScreenMode();
		
		const onResize = (): void =>
		{
			const nextScreenMode = getCurrentScreenMode();
			
			if ( nextScreenMode < ScreenMode.NOTEBOOK )
			{
				window.requestAnimationFrame( onFrame );
			}
			else if ( screenMode < ScreenMode.NOTEBOOK )
			{
				parent.style.height = '';
			}
			
			screenMode = nextScreenMode;
		};
		
		window.addEventListener( 'resize', onResize );
		onResize();
	}
}

/**
 * Module.
 */
export {
	main as default,
};



// WEBPACK FOOTER //
// ./app/Routes/Index/Slideshow/index.ts